import {
    buildAgencyDigest,
    IAgencyClient,
    IAgencyDigest,
    IAgencyFinding
} from './agencyDigest';
import { DEFAULT_WINDOW_WEEKS } from './agencyThresholds';

/**
 * An invented caseload with enough history to fill every panel, for tests and for the admin-only
 * demo setting. Never a stand-in for a real caseload: a real one too young to chart shows each panel
 * waiting instead. Every surface that draws it does so under the demo banner, and the names below
 * are invented, so a screenshot that escapes must not be mistakable for an agency's real book. How
 * to reach it: docs/agency-insights-demo.md.
 *
 * The people are given ordinary names rather than "Client A" because a demo of a caseload tool has
 * to look like a caseload to be worth showing, and initials in a honeycomb cell need real initials.
 * Common names on purpose: a distinctive invented name is likelier to land on one identifiable
 * person than a name thousands share. They are deliberately not paired with photographs either, since
 * an invented face is far easier to mistake for a real person than a name under the Demo banner.
 *
 * It is generated by running invented findings through `buildAgencyDigest`, not by hand-writing the
 * output, so the sample and the real thing are the same arithmetic and cannot drift apart.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const SAMPLE_CLIENTS: { name: string; codeStatus: boolean; insurance: boolean; identity: boolean }[] = [
    { name: 'Margaret Wilson', codeStatus: true, insurance: true, identity: true },
    { name: 'Robert Anderson', codeStatus: true, insurance: true, identity: false },
    { name: 'Dorothy Miller', codeStatus: false, insurance: true, identity: true },
    { name: 'James Thompson', codeStatus: true, insurance: false, identity: false },
    { name: 'Patricia Davis', codeStatus: false, insurance: false, identity: false },
    { name: 'Charles Brown', codeStatus: true, insurance: true, identity: true },
    { name: 'Barbara Johnson', codeStatus: true, insurance: true, identity: true },
    { name: 'Richard Moore', codeStatus: false, insurance: true, identity: false },
    { name: 'Helen Clark', codeStatus: true, insurance: true, identity: false },
    { name: 'George Baker', codeStatus: true, insurance: false, identity: true }
];

export const SAMPLE_CLIENT_NAMES = SAMPLE_CLIENTS.map(client => client.name);

/**
 * Findings as day offsets so the sample always lands inside the window wherever "now" falls.
 *
 * Nothing here may sit further back than 74 days. The window is twelve whole buckets ending on the
 * current week, so it reaches back 77 days when read on a Monday and 83 on a Sunday: an offset
 * between those two would silently vanish from the sample on some days of the week and not others.
 *
 * The shape is deliberate rather than uniform, because a flat caseload teaches the reader nothing.
 * Four people carry the sample's story and the rest are ordinary:
 *
 *   James Thompson (3) is the hard case. A fall, then an ER trip, then a hospital stay, and he is
 *   still off his baseline now. He is the coral cell somebody has to open.
 *
 *   Barbara Johnson (6) is the recovery. A difficult run through the baseline weeks, a fall 11
 *   days back, a note about the dose change two days later, then no findings in the last 7 days.
 *   That puts her behind Back From a Decline, but the fall is recent enough to count, so the map
 *   still draws her coral. She and Richard are the pair that show the difference a recent incident
 *   makes.
 *
 *   Margaret Wilson (0) settled. A dip about a month ago and every part of daily life quiet since,
 *   so less is raised about her now than her own usual, which is what Settled Below Their Usual
 *   counts. The dip sits more than 28 days back so that it is in her earlier weeks whatever day the
 *   sample is read: inside the recent weeks on some days, it made her Settled only on Mondays.
 *
 *   Richard Moore (7) is a settled cell. A difficult stretch through the baseline weeks around a
 *   house move, then nothing at all, and crucially nothing that counts as an incident anywhere in
 *   the window. A recent incident takes a client to declining whatever else the record says, so the
 *   settled client has to be one nothing has happened to lately.
 *
 *   Three clients (0, 6, 7) have nothing in the last seven days, so family participation is 7 of 10
 *   rather than the flat 10 of 10 an invented caseload defaults to. A demo where everyone
 *   participates is not a demo of a participation metric.
 */
const SAMPLE_FINDINGS: { client: number; daysAgo: number; type: IAgencyFinding['type']; title: string; narrative: string }[] = [
    // The baseline weeks. Documentation gaps cluster early because that is the part an agency closes
    // first, and the two difficult cases build their history here.
    { client: 2, daysAgo: 74, type: 'gap', title: 'No code status on file', narrative: 'Nothing recorded for code status.' },
    { client: 6, daysAgo: 72, type: 'anomaly', title: 'Sleeping badly most nights', narrative: 'Awake several times, tired by the afternoon.' },
    { client: 4, daysAgo: 71, type: 'gap', title: 'Insurance card not on file', narrative: 'No card image or member id.' },
    { client: 7, daysAgo: 70, type: 'gap', title: 'No identity document', narrative: 'Nothing on file to verify identity.' },
    { client: 7, daysAgo: 62, type: 'anomaly', title: 'Anxious about the move', narrative: 'Raised it at three visits running.' },
    { client: 7, daysAgo: 55, type: 'anomaly', title: 'Sleeping poorly before the move', narrative: 'Awake early most mornings.' },
    { client: 3, daysAgo: 69, type: 'anomaly', title: 'Missed two scheduled visits', narrative: 'Two visits in a row went unattended.' },
    { client: 6, daysAgo: 66, type: 'anomaly', title: 'Low mood in the mornings', narrative: 'Quiet and tearful when the aide arrives.' },
    { client: 5, daysAgo: 64, type: 'recommendation', title: 'Consider a medication review', narrative: 'Four active prescriptions and no recent review.' },
    { client: 4, daysAgo: 63, type: 'gap', title: 'Medicaid redetermination approaching', narrative: 'Redetermination date is not recorded.' },
    { client: 1, daysAgo: 61, type: 'anomaly', title: 'Weight down since last visit', narrative: 'Noted lighter than the previous two visits.' },
    { client: 6, daysAgo: 60, type: 'anomaly', title: 'Eating very little at supper', narrative: 'Left most of the plate three evenings running.' },
    { client: 8, daysAgo: 58, type: 'trend', title: 'Sleeping later each week', narrative: 'Wake time has drifted later across the month.' },
    { client: 3, daysAgo: 55, type: 'anomaly', title: 'Unsteady on the stairs', narrative: 'Case manager noted unsteadiness.' },
    // Margaret's usual: a little forgetful now and then, then a dip about a month ago with two parts
    // of daily life moving together, and nothing raised since.
    { client: 0, daysAgo: 57, type: 'anomaly', title: 'Forgot the new aide\'s name', narrative: 'Asked who she was at the start of the visit.' },
    { client: 0, daysAgo: 50, type: 'anomaly', title: 'Confused by the new pill organizer', narrative: 'Needed it explained a second time.' },
    { client: 0, daysAgo: 31, type: 'anomaly', title: 'Forgot her morning tablets twice', narrative: 'Found still in the organizer at midday.' },
    { client: 0, daysAgo: 30, type: 'anomaly', title: 'Skipping breakfast', narrative: 'Only tea when the aide arrived, twice this week.' },
    { client: 0, daysAgo: 29, type: 'anomaly', title: 'Confused about the day of the week', narrative: 'Asked the aide twice in one visit.' },
    { client: 6, daysAgo: 54, type: 'anomaly', title: 'Hip pain getting up', narrative: 'Worse in the mornings, easier by lunchtime.' },
    { client: 2, daysAgo: 49, type: 'gap', title: 'Code status referenced but unconfirmed', narrative: 'A DNR is mentioned but nobody has verified it.' },
    { client: 5, daysAgo: 47, type: 'anomaly', title: 'Blood pressure running high', narrative: 'Three readings above the usual range.' },
    { client: 6, daysAgo: 47, type: 'anomaly', title: 'Confused about the day', narrative: 'Asked twice in one visit.' },
    { client: 9, daysAgo: 44, type: 'trend', title: 'Fewer social visits logged', narrative: 'Visitors noted less often than earlier in the year.' },
    { client: 6, daysAgo: 41, type: 'anomaly', title: 'Withdrawn with the family', narrative: 'Daughter noticed she was quieter than usual.' },
    { client: 3, daysAgo: 41, type: 'anomaly', title: 'Fall in the bathroom', narrative: 'Client fell overnight; nobody was called.' },
    { client: 1, daysAgo: 38, type: 'anomaly', title: 'Appetite down again', narrative: 'Eating less than usual at two visits.' },
    { client: 6, daysAgo: 35, type: 'anomaly', title: 'Restless and up in the night', narrative: 'Wandering to the kitchen after midnight.' },
    { client: 3, daysAgo: 35, type: 'anomaly', title: 'ER visit after a second fall', narrative: 'Taken to the emergency department and discharged the same day.' },
    { client: 7, daysAgo: 40, type: 'anomaly', title: 'Eating less while unsettled', narrative: 'Smaller portions at most meals.' },
    { client: 7, daysAgo: 33, type: 'gap', title: 'Pharmacy contact missing', narrative: 'No pharmacy recorded on the care map.' },
    { client: 6, daysAgo: 30, type: 'recommendation', title: 'Medication review scheduled', narrative: 'Prescriber asked to look at the night-time dose.' },
    { client: 5, daysAgo: 30, type: 'trend', title: 'Steady since the medication change', narrative: 'No new observations in three weeks.' },

    // The recent weeks. This is the window the per-client diagram reads, so the texture here is what
    // the tab actually shows.
    { client: 3, daysAgo: 27, type: 'anomaly', title: 'Confusion reported by the visiting nurse', narrative: 'Nurse noted disorientation in the afternoon.' },
    { client: 3, daysAgo: 20, type: 'anomaly', title: 'Missed medication doses', narrative: 'Pill organizer still full midweek.' },
    { client: 8, daysAgo: 18, type: 'anomaly', title: 'Reported feeling dizzy standing up', narrative: 'Mentioned twice at the same visit.' },
    { client: 3, daysAgo: 16, type: 'anomaly', title: 'Eating less at supper', narrative: 'Left most of the plate two evenings running.' },
    { client: 5, daysAgo: 13, type: 'anomaly', title: 'Complained of hip pain', narrative: 'Worse getting out of the chair.' },
    { client: 3, daysAgo: 12, type: 'anomaly', title: 'Hospital discharge summary received', narrative: 'Overnight stay for observation after the fall.' },

    // Barbara's fall and a note two days later, then no findings. A quiet week after an incident,
    // with the household still writing, is what a recovery looks like on a record, and it is why she
    // is on Back From a Decline.
    { client: 6, daysAgo: 11, type: 'anomaly', title: 'Fall getting out of the chair', narrative: 'Went down in the front room; no ambulance needed.' },
    { client: 6, daysAgo: 9, type: 'trend', title: 'Settled since the night-time dose changed', narrative: 'Sleeping through and steadier on her feet.' },

    { client: 2, daysAgo: 11, type: 'anomaly', title: 'Sleeping through the afternoon', narrative: 'Napping longer than she used to.' },
    { client: 9, daysAgo: 10, type: 'gap', title: 'Insurance expires next month', narrative: 'Renewal not yet recorded.' },

    // The last seven days. Seven of the ten clients have something here, which is what family
    // participation counts.
    { client: 9, daysAgo: 6, type: 'trend', title: 'Fewer visitors this month', narrative: 'Family visits noted less often than in the spring.' },
    { client: 4, daysAgo: 6, type: 'gap', title: 'No identity document', narrative: 'Still nothing on file to verify identity.' },
    { client: 5, daysAgo: 6, type: 'anomaly', title: 'Quieter than usual on the phone', narrative: 'Daughter mentioned he sounded flat.' },
    { client: 3, daysAgo: 4, type: 'anomaly', title: 'Unsteady on her feet', narrative: 'Case manager noted unsteadiness again.' },
    { client: 8, daysAgo: 3, type: 'anomaly', title: 'Poor appetite at lunch', narrative: 'Left most of the meal again.' },
    { client: 1, daysAgo: 2, type: 'anomaly', title: 'Declined the afternoon visit', narrative: 'Second refusal this fortnight.' },
    { client: 3, daysAgo: 2, type: 'anomaly', title: 'Fall in the kitchen', narrative: 'Slipped reaching for a cupboard; bruised, not hurt.' },
    { client: 2, daysAgo: 1, type: 'anomaly', title: 'Agitated in the evening', narrative: 'Unsettled for an hour after the aide left.' }
];

export const buildSampleDigest = (now: Date): IAgencyDigest => {
    const clients: IAgencyClient[] = SAMPLE_CLIENTS.map((client, index) => ({
        careMapId: `sample-${index}`,
        clientName: client.name,
        coverageKnown: true,
        hasConfirmedCodeStatus: client.codeStatus,
        hasInsuranceOnFile: client.insurance,
        hasIdentityDocument: client.identity,
        // Every sample household is still writing, so the sample shows each standing rather than a
        // caseload of clients nothing can be said about.
        lastRecordedAt: new Date(now.getTime() - DAY_MS).toISOString()
    }));

    const findings: IAgencyFinding[] = SAMPLE_FINDINGS.map(finding => ({
        careMapId: `sample-${finding.client}`,
        clientName: SAMPLE_CLIENTS[finding.client].name,
        type: finding.type,
        confidence: 'medium',
        title: finding.title,
        narrative: finding.narrative,
        observedOn: new Date(now.getTime() - finding.daysAgo * DAY_MS).toISOString()
    }));

    return {
        ...buildAgencyDigest({ findings, clients, now, windowWeeks: DEFAULT_WINDOW_WEEKS }),
        // The sample's clients are invented, so no real client is missing from it.
        unreadableCount: 0
    };
};
